// REQ-P1-06b: タスクプロジェクト詳細画面の契約型付きGET API。

import { api, type paths } from './client';
import { ApiStatusError } from './templates';

type TaskProjectDetailResponse = NonNullable<
  paths['/admin/task-projects/{projectId}']['get']['responses'][200]['content']
>['application/json'];
type PrimaryEventTaskProjectResponse = NonNullable<
  paths['/admin/task-projects/event/{eventUuid}/primary']['get']['responses'][200]['content']
>['application/json'];
type TaskProjectMembersResponse = NonNullable<
  paths['/admin/task-projects/{projectId}/members']['get']['responses'][200]['content']
>['application/json'];
type TaskProjectMutationRequest = NonNullable<
  paths['/admin/task-projects/{projectId}']['patch']['requestBody']
>['content']['application/json'];
type TaskProjectMemberMutationRequest = NonNullable<
  paths['/admin/task-projects/{projectId}/members']['post']['requestBody']
>['content']['application/json'];
type TaskTemplateApplyRequest = NonNullable<
  paths['/admin/task-projects/{projectId}/apply-template']['post']['requestBody']
>['content']['application/json'];
type TaskSectionMutationRequest = NonNullable<
  paths['/admin/task-projects/{projectId}/sections']['post']['requestBody']
>['content']['application/json'];
type TaskSectionReorderRequest = NonNullable<
  paths['/admin/task-projects/{projectId}/sections/reorder']['post']['requestBody']
>['content']['application/json'];
type TaskItemMutationRequest = NonNullable<
  paths['/admin/task-items']['post']['requestBody']
>['content']['application/json'];
type TaskProjectReorderRequest = NonNullable<
  paths['/admin/task-projects/{projectId}/reorder']['post']['requestBody']
>['content']['application/json'];
type AsanaCsvImportRequest = NonNullable<
  paths['/admin/task-projects/{projectId}/import/asana-csv']['post']['requestBody']
>['content']['multipart/form-data'];
type TaskCommentsResponse = NonNullable<
  paths['/admin/task-items/{taskId}/comments']['get']['responses'][200]['content']
>['application/json'];
type TaskCommentMutationRequest = NonNullable<
  paths['/admin/task-items/{taskId}/comments']['post']['requestBody']
>['content']['application/json'];
type TaskCommentReactionRequest = NonNullable<
  paths['/admin/task-comments/{commentId}/reactions']['post']['requestBody']
>['content']['application/json'];
type TaskDependenciesResponse = NonNullable<
  paths['/admin/task-items/{taskId}/dependencies']['get']['responses'][200]['content']
>['application/json'];
type TaskDependencyMutationRequest = NonNullable<
  paths['/admin/task-items/{taskId}/dependencies']['post']['requestBody']
>['content']['application/json'];
type TaskAttachmentsResponse = NonNullable<
  paths['/admin/task-items/{taskId}/attachments']['get']['responses'][200]['content']
>['application/json'];
type TaskAttachmentUploadRequest = NonNullable<
  paths['/admin/task-items/{taskId}/attachments']['post']['requestBody']
>['content']['multipart/form-data'];

async function readApiErrorMessage(response: Response, fallback: string) {
  try {
    const json = await response.clone().json();
    return String(json?.message || '').trim() || fallback;
  } catch (_error) {
    return fallback;
  }
}

async function readOptionalJson(data: unknown, response: Response) {
  if (data && typeof data === 'object') return data as Record<string, unknown>;
  try {
    const json = await response.clone().json();
    return json && typeof json === 'object' ? (json as Record<string, unknown>) : {};
  } catch (_error) {
    return {};
  }
}

export async function getTaskProjectDetail(projectId: string): Promise<TaskProjectDetailResponse> {
  const { data, response } = await api.GET('/admin/task-projects/{projectId}', {
    params: { path: { projectId } },
  });
  if (!response.ok || !data) {
    throw new Error(await readApiErrorMessage(response, 'プロジェクトの取得に失敗しました。'));
  }
  return data as TaskProjectDetailResponse;
}

export async function getPrimaryEventTaskProject(
  eventUuid: string
): Promise<PrimaryEventTaskProjectResponse> {
  const { data, response } = await api.GET('/admin/task-projects/event/{eventUuid}/primary', {
    params: { path: { eventUuid } },
  });
  if (!response.ok || !data) {
    const error = new ApiStatusError(response.status);
    error.message = await readApiErrorMessage(response, 'イベントタスク情報の取得に失敗しました。');
    throw error;
  }
  return data as PrimaryEventTaskProjectResponse;
}

export async function ensurePrimaryEventTaskProject(eventUuid: string) {
  const { data, response } = await api.POST('/admin/task-projects/event/{eventUuid}/ensure', {
    params: { path: { eventUuid } },
    body: {},
  });
  if (!response.ok) {
    throw new Error(await readApiErrorMessage(response, 'イベントタスクの作成に失敗しました。'));
  }
  return readOptionalJson(data, response);
}

export async function updateTaskProject(projectId: string, body: TaskProjectMutationRequest) {
  const { data, response } = await api.PATCH('/admin/task-projects/{projectId}', {
    params: { path: { projectId } },
    body,
  });
  if (!response.ok) {
    throw new Error(await readApiErrorMessage(response, 'プロジェクト設定の更新に失敗しました。'));
  }
  return readOptionalJson(data, response);
}

export async function deleteTaskProject(projectId: string) {
  const { data, response } = await api.DELETE('/admin/task-projects/{projectId}', {
    params: { path: { projectId } },
  });
  if (!response.ok) {
    throw new Error(await readApiErrorMessage(response, 'プロジェクト削除に失敗しました。'));
  }
  return readOptionalJson(data, response);
}

export async function listTaskProjectMembers(projectId: string): Promise<TaskProjectMembersResponse> {
  const { data, response } = await api.GET('/admin/task-projects/{projectId}/members', {
    params: { path: { projectId } },
  });
  if (!response.ok || !data) {
    throw new Error(await readApiErrorMessage(response, 'プロジェクトメンバーの取得に失敗しました。'));
  }
  return data as TaskProjectMembersResponse;
}

export async function saveTaskProjectMember(projectId: string, body: TaskProjectMemberMutationRequest) {
  const { data, response } = await api.POST('/admin/task-projects/{projectId}/members', {
    params: { path: { projectId } },
    body,
  });
  if (!response.ok) {
    throw new Error(await readApiErrorMessage(response, 'プロジェクトメンバーの更新に失敗しました。'));
  }
  return readOptionalJson(data, response);
}

export async function applyTaskTemplateToProject(projectId: string, body: TaskTemplateApplyRequest) {
  const { data, response } = await api.POST('/admin/task-projects/{projectId}/apply-template', {
    params: { path: { projectId } },
    body,
  });
  if (!response.ok) {
    throw new Error(await readApiErrorMessage(response, 'テンプレート適用に失敗しました。'));
  }
  return readOptionalJson(data, response);
}

export async function createTaskProjectSection(projectId: string, body: TaskSectionMutationRequest) {
  const { data, response } = await api.POST('/admin/task-projects/{projectId}/sections', {
    params: { path: { projectId } },
    body,
  });
  if (!response.ok) {
    throw new Error(await readApiErrorMessage(response, 'セクションの作成に失敗しました。'));
  }
  return readOptionalJson(data, response);
}

export async function updateTaskProjectSection(
  projectId: string,
  sectionId: string,
  body: TaskSectionMutationRequest
) {
  const { data, response } = await api.PATCH('/admin/task-projects/{projectId}/sections/{sectionId}', {
    params: { path: { projectId, sectionId } },
    body,
  });
  if (!response.ok) {
    throw new Error(await readApiErrorMessage(response, 'セクション名の変更に失敗しました。'));
  }
  return readOptionalJson(data, response);
}

export async function deleteTaskProjectSection(projectId: string, sectionId: string) {
  const { data, response } = await api.DELETE('/admin/task-projects/{projectId}/sections/{sectionId}', {
    params: { path: { projectId, sectionId } },
  });
  if (!response.ok) {
    throw new Error(await readApiErrorMessage(response, 'セクションの削除に失敗しました。'));
  }
  return readOptionalJson(data, response);
}

export async function reorderTaskProjectSections(projectId: string, body: TaskSectionReorderRequest) {
  const { data, response } = await api.POST('/admin/task-projects/{projectId}/sections/reorder', {
    params: { path: { projectId } },
    body,
  });
  if (!response.ok) {
    throw new Error(await readApiErrorMessage(response, 'セクションの並び替えに失敗しました。'));
  }
  return readOptionalJson(data, response);
}

export async function createTaskItem(body: TaskItemMutationRequest) {
  const { data, response } = await api.POST('/admin/task-items', {
    body,
  });
  if (!response.ok) {
    throw new Error(await readApiErrorMessage(response, 'タスクの作成に失敗しました。'));
  }
  return readOptionalJson(data, response);
}

export async function updateTaskItem(taskId: string, body: TaskItemMutationRequest) {
  const { data, response } = await api.PATCH('/admin/task-items/{taskId}', {
    params: { path: { taskId } },
    body,
  });
  if (!response.ok) {
    throw new Error(await readApiErrorMessage(response, 'タスク更新に失敗しました。'));
  }
  return readOptionalJson(data, response);
}

export async function deleteTaskItem(taskId: string) {
  const { data, response } = await api.DELETE('/admin/task-items/{taskId}', {
    params: { path: { taskId } },
  });
  if (!response.ok) {
    throw new Error(await readApiErrorMessage(response, 'タスクの削除に失敗しました。'));
  }
  return readOptionalJson(data, response);
}

export async function reorderTaskProject(projectId: string, body: TaskProjectReorderRequest) {
  const { data, response } = await api.POST('/admin/task-projects/{projectId}/reorder', {
    params: { path: { projectId } },
    body,
  });
  if (!response.ok) {
    throw new Error(await readApiErrorMessage(response, 'タスクの並び替えに失敗しました。'));
  }
  return readOptionalJson(data, response);
}

export async function importAsanaCsvIntoTaskProject(projectId: string, body: FormData) {
  const { data, error, response } = await api.POST('/admin/task-projects/{projectId}/import/asana-csv', {
    params: { path: { projectId } },
    // OpenAPI models binary fields as strings; the runtime payload must stay multipart.
    body: body as unknown as AsanaCsvImportRequest,
  });
  if (!response.ok) {
    const payload = error as { message?: string } | undefined;
    throw new Error(payload?.message || 'Asana CSVの取り込みに失敗しました。');
  }
  return readOptionalJson(data, response);
}

export async function listTaskComments(taskId: string): Promise<TaskCommentsResponse> {
  const { data, response } = await api.GET('/admin/task-items/{taskId}/comments', {
    params: { path: { taskId } },
  });
  if (!response.ok || !data) {
    throw new Error(await readApiErrorMessage(response, 'コメントの取得に失敗しました。'));
  }
  return data as TaskCommentsResponse;
}

export async function createTaskComment(taskId: string, body: TaskCommentMutationRequest) {
  const { data, response } = await api.POST('/admin/task-items/{taskId}/comments', {
    params: { path: { taskId } },
    body,
  });
  if (!response.ok) {
    throw new Error(await readApiErrorMessage(response, 'コメント投稿に失敗しました。'));
  }
  return readOptionalJson(data, response);
}

export async function toggleTaskCommentReaction(commentId: string, body: TaskCommentReactionRequest) {
  const { data, response } = await api.POST('/admin/task-comments/{commentId}/reactions', {
    params: { path: { commentId } },
    body,
  });
  if (!response.ok) {
    throw new Error(await readApiErrorMessage(response, 'コメントリアクションの更新に失敗しました。'));
  }
  return readOptionalJson(data, response);
}

export async function listTaskDependencies(taskId: string): Promise<TaskDependenciesResponse> {
  const { data, response } = await api.GET('/admin/task-items/{taskId}/dependencies', {
    params: { path: { taskId } },
  });
  if (!response.ok || !data) {
    throw new Error(await readApiErrorMessage(response, '依存関係の取得に失敗しました。'));
  }
  return data as TaskDependenciesResponse;
}

export async function createTaskDependency(taskId: string, body: TaskDependencyMutationRequest) {
  const { data, response } = await api.POST('/admin/task-items/{taskId}/dependencies', {
    params: { path: { taskId } },
    body,
  });
  if (!response.ok) {
    throw new Error(await readApiErrorMessage(response, '依存関係の追加に失敗しました。'));
  }
  return readOptionalJson(data, response);
}

export async function deleteTaskDependency(taskId: string, predecessorTaskId: string) {
  const { data, response } = await api.DELETE('/admin/task-items/{taskId}/dependencies/{predecessorTaskId}', {
    params: { path: { taskId, predecessorTaskId } },
  });
  if (!response.ok) {
    throw new Error(await readApiErrorMessage(response, '依存関係の削除に失敗しました。'));
  }
  return readOptionalJson(data, response);
}

export async function listTaskAttachments(taskId: string): Promise<TaskAttachmentsResponse> {
  const { data, response } = await api.GET('/admin/task-items/{taskId}/attachments', {
    params: { path: { taskId } },
  });
  if (!response.ok || !data) {
    throw new Error(await readApiErrorMessage(response, '添付ファイル一覧の取得に失敗しました。'));
  }
  return data as TaskAttachmentsResponse;
}

export async function uploadTaskAttachment(taskId: string, body: TaskAttachmentUploadRequest) {
  const { data, response } = await api.POST('/admin/task-items/{taskId}/attachments', {
    params: { path: { taskId } },
    body,
  });
  if (!response.ok) {
    throw new Error(await readApiErrorMessage(response, '添付ファイルの登録に失敗しました。'));
  }
  return readOptionalJson(data, response);
}

export async function deleteTaskAttachment(attachmentId: string) {
  const { data, response } = await api.DELETE('/admin/task-attachments/{attachmentId}', {
    params: { path: { attachmentId } },
  });
  if (!response.ok) {
    throw new Error(await readApiErrorMessage(response, '添付ファイルの削除に失敗しました。'));
  }
  return readOptionalJson(data, response);
}

export async function downloadTaskAttachment(attachmentId: string): Promise<Blob> {
  const { data, response } = await api.GET('/admin/task-attachments/{attachmentId}/download', {
    params: { path: { attachmentId } },
    parseAs: 'blob',
  });
  if (!response.ok) {
    throw new Error(await readApiErrorMessage(response, '添付ファイルのダウンロードに失敗しました。'));
  }
  if (data instanceof Blob) return data;
  if (typeof data === 'string') return new Blob([data]);
  return response.clone().blob();
}

export async function listTaskMentionCandidates(projectId: string, q = '', after = 0) {
  const { data, response } = await api.GET('/admin/task-projects/{projectId}/mention-candidates', {
    params: { path: { projectId }, query: { q, after } }, signal: AbortSignal.timeout(15000),
  });
  if (!response.ok || !data || !Array.isArray(data.candidates)) throw new Error('メンション候補を取得できませんでした。');
  return data;
}
